import React, { useEffect, useState } from "react"
import { Row } from "reactstrap"
import {
    merchantSelectors,
    PageContainer,
    PageWrapper,
    sortAscending,
    StandardButton,
    StandardButtonWithSpinner,
    TPlatformValue,
    useSelector,
} from "swiipe.portal.shared"
import SpinnerContainer from "../../components/buttons/SpinnerContainer"
import DropDownList from "../../components/form/input/DropDownList"
import CodeEditor from "../../components/form/syntax/CodeEditor"
import WebshopSelect from "../../components/merchant/WebshopSelect"
import PageBoxSection from "../../components/page/PageBoxSection"
import { useRequiredUserRelations } from "../../services/userRelationService"
import { StoreState } from "../../store/StoreState"
import useReduxDispatch from "../../store/useReduxDispatch"
import { rightPad } from "../../util/stringUtil"
import FieldsetFormGroup from "./../../components/form/FieldsetFormGroup"
import { IPluginAssetSelection, pluginSelectors, TPluginAssetName, TPluginAssetType } from "./../../store/reducers/pluginReducer"
import {
    enableFastUpdateThunk,
    ensureWebshopAssetListThunk,
    ensureWebshopAssetsSettingsThunk,
    getPluginAssetThunk,
    getPluginInfoThunk,
    updatePluginAssetThunk,
} from "./../../store/thunks/webshopAssetThunks"
import "./WebshopAssetEditPage.scss"

interface IWebshopAssetEditPageProps {}

const standardPlatforms: TPlatformValue[] = ["magento", "magento2", "prestashop", "woocommerce"]
const iframeAssets: TPluginAssetName[] = ["comps", "orchestrator"]

const WebshopAssetEditPage = ({}: IWebshopAssetEditPageProps) => {
    useRequiredUserRelations([{ requiredType: "Merchant", requiredRole: 90 }])
    const pluginInfoData = useSelector(pluginSelectors.pluginInfoData)
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const selectedWebshopId = useSelector(merchantSelectors.selectedWebshopId) ?? ""
    const webshops = merchantDetails?.webshops || []
    const settings = useSelector((state: StoreState) => pluginSelectors.webshopAssetsSettings(state, selectedWebshopId))
    const assetList = useSelector((state: StoreState) => pluginSelectors.pluginAssetList(state, selectedWebshopId))

    const platformId = selectedWebshopId ? webshops.find((w) => w.webshopId === selectedWebshopId)?.platform ?? "" : ""

    const dispatch = useReduxDispatch()
    useEffect(() => {
        if (!pluginInfoData) {
            dispatch(getPluginInfoThunk())
        }
    }, [])

    useEffect(() => {
        if (!settings) {
            dispatch(ensureWebshopAssetsSettingsThunk(selectedWebshopId))
        }
        if (!assetList) {
            dispatch(ensureWebshopAssetListThunk(selectedWebshopId, platformId))
        }
    }, [selectedWebshopId])

    const [timeCounter, setTimeCounter] = useState(0)
    const [content, setContent] = useState("")
    const [isSaving, setIsSaving] = useState(false)
    const [countryId, setCountryId] = useState("")
    const [pluginName, setPluginName] = useState("")
    const [assetName, setAssetName] = useState<TPluginAssetName>("checkout")
    const [assetType, setAssetType] = useState<TPluginAssetType>("style")

    useEffect(() => {
        if (platformId && assetName && !standardPlatforms.includes(platformId) && !iframeAssets.includes(assetName)) {
            setAssetName("comps")
        }
        if (platformId && assetName && standardPlatforms.includes(platformId) && iframeAssets.includes(assetName)) {
            setAssetName("checkout")
        }
    }, [platformId])

    const pluginAssetSelection: IPluginAssetSelection = {
        webshopId: selectedWebshopId,
        platform: platformId,
        country: countryId,
        plugin: pluginName,
        assetName,
        assetType,
    }

    const pluginAsset = useSelector(pluginSelectors.pluginAsset(pluginAssetSelection))

    useEffect(() => {
        setContent(pluginAsset?.content ?? "")
    }, [pluginAsset])

    useEffect(() => {
        if (settings && settings.fastUpdateUntil && settings.fastUpdateUntil > new Date()) {
            setTimeout(() => {
                setTimeCounter(timeCounter + 1)
            }, 1000 * 60)
        }
    }, [timeCounter, settings])

    useEffect(() => {
        if (!pluginInfoData || pluginInfoData.length === 0) {
            setCountryId("")
            setPluginName("")
            return
        }
        let updatedVars = false

        if (!selectedWebshopId) {
            return
        }

        // Find platform
        const platform = pluginInfoData.find((d) => d.id === platformId)
        if (!platform) {
            return
        }
        if (platform.countries.length === 0) {
            setCountryId("")
            setPluginName("")
            return
        }
        // Find country
        let country = platform.countries.find((d) => d.id === countryId)
        if (!country) {
            country = platform.countries[0]
            setCountryId(country.id)
            updatedVars = true
        }
        if (country.plugins.length === 0) {
            setPluginName("")
            return
        }
        // Find plugin
        let selectedPlugin = country.plugins.find((p) => p === pluginName)
        if (!selectedPlugin) {
            selectedPlugin = country.plugins.find((p) => p === "checkout")
            if (!selectedPlugin) {
                selectedPlugin = country.plugins[0]
            }
            setPluginName(selectedPlugin)
            updatedVars = true
        }
        // Ensure assetName
        if (!assetName) {
            setAssetName("checkout")
            updatedVars = true
        }
        // Ensure assetType
        if (!assetType) {
            setAssetType("style")
            updatedVars = true
        }
        if (!updatedVars) {
            dispatch(getPluginAssetThunk(pluginAssetSelection))
        }
    }, [merchantDetails, selectedWebshopId, pluginInfoData, platformId, countryId, pluginName, assetName, assetType])

    if (!pluginInfoData || !merchantDetails) {
        return null
    }

    const save = async () => {
        try {
            setIsSaving(true)
            await dispatch(updatePluginAssetThunk(pluginAssetSelection, content))
        } catch (err) {
            // Catch to stop showing spinner
        }
        setIsSaving(false)
    }

    const platforms = pluginInfoData
    const platform = pluginInfoData.find((p) => p.id === platformId)
    const countries = platform ? platform.countries || [] : []
    const country = countries.find((p) => p.id === countryId)
    const plugins = country ? country.plugins || [] : []
    const plugin = plugins.find((p) => p === pluginName)
    const assets: TPluginAssetName[] =
        plugin && platformId
            ? standardPlatforms.includes(platformId)
                ? ["checkout", "thankyou", "product", "admin", "return", ...iframeAssets]
                : iframeAssets
            : []
    const assetTypes: TPluginAssetType[] = plugin ? ["script", "style"] : []

    return (
        <PageWrapper fullWidth>
            <PageContainer classNameBody="webshop-asset-edit-page" title="Plugin asset editor">
                <SpinnerContainer aboveContent showSpinner={isSaving}>
                    <div className="webshop-asset-selector-container">
                        <WebshopSelect
                            className="mb-3"
                            merchantDetails={merchantDetails}
                            hideTitle
                            renderAsPage={false}
                            initialWebshopId={selectedWebshopId}
                        />
                        <Row>
                            <FieldsetFormGroup field col2>
                                {countries.length > 0 && (
                                    <DropDownList
                                        placeholder={"Country"}
                                        value={countryId}
                                        onChange={(e) => setCountryId(e.target.value)}
                                        options={countries.map((d) => ({ text: d.displayName, value: d.id }))}
                                    />
                                )}
                            </FieldsetFormGroup>
                            <FieldsetFormGroup field col2>
                                {plugins.length > 0 && (
                                    <DropDownList
                                        placeholder={"Plugin Name"}
                                        value={pluginName}
                                        onChange={(e) => setPluginName(e.target.value)}
                                        options={plugins.map((d) => ({ text: d, value: d }))}
                                    />
                                )}
                            </FieldsetFormGroup>
                        </Row>
                        <Row>
                            <FieldsetFormGroup field col2>
                                {plugin && (
                                    <DropDownList
                                        placeholder={"Webpage for asset"}
                                        value={assetName}
                                        onChange={(e) => setAssetName(e.target.value as TPluginAssetName)}
                                        options={assets.map((d) => ({ text: d, value: d }))}
                                    />
                                )}
                            </FieldsetFormGroup>
                            <FieldsetFormGroup field col2>
                                {plugin && (
                                    <DropDownList
                                        placeholder={"Type of asset"}
                                        value={assetType}
                                        onChange={(e) => setAssetType(e.target.value as TPluginAssetType)}
                                        options={assetTypes.map((d) => ({ text: d, value: d }))}
                                    />
                                )}
                            </FieldsetFormGroup>
                        </Row>
                    </div>
                    <div className="webshop-asset-editor mt-2">
                        <SpinnerContainer showSpinner={typeof pluginAsset === "undefined"}>
                            <CodeEditor
                                code={content}
                                onChange={(code) => setContent(code)}
                                language={assetType === "script" ? "js" : "css"}
                                minHeight={300}
                                onSaveKey={() => {
                                    save()
                                }}
                            />
                        </SpinnerContainer>
                        <FieldsetFormGroup className="button-container mt-2">
                            <StandardButtonWithSpinner
                                invertedPurple
                                title="Set fast update for 1 hour"
                                onClick={async () => {
                                    try {
                                        await dispatch(enableFastUpdateThunk(selectedWebshopId))
                                    } catch (ex) {
                                        // Ignore
                                    }
                                }}
                            />
                            <SpinnerContainer showSpinner={isSaving}>
                                <StandardButton
                                    title="Update asset"
                                    onClick={async () => {
                                        save()
                                    }}
                                />
                            </SpinnerContainer>
                        </FieldsetFormGroup>
                        {settings && settings.fastUpdateUntil && settings.fastUpdateUntil > new Date() && (
                            <div className="fast-update-time">
                                <span>
                                    Fast update enabled for{" "}
                                    {Math.ceil((settings.fastUpdateUntil.getTime() - new Date().getTime()) / (1000 * 60))} minutes
                                </span>
                            </div>
                        )}
                    </div>
                </SpinnerContainer>
                {pluginAsset?.createdByUserId && (
                    <div>Created by: {pluginAsset.createdByUserEmail ?? pluginAsset.createdByUserId}</div>
                )}
                {assetList && (
                    <PageBoxSection title="Assets">
                        <div className="webshop-asset-edit-page__asset-list-container">
                            <div className="webshop-asset-edit-page__asset-list-asset webshop-asset-edit-page__asset-list-asset--header mt-3">
                                <div>Country:</div>
                                <div>Name:</div>
                                <div>Type:</div>
                                <div>Plugin:</div>
                                <div>Version:</div>
                            </div>
                            {sortAscending(
                                assetList,
                                (a) =>
                                    rightPad(a.country === "int" ? "aaa" : a.country, 10, "0") +
                                    rightPad(a.assetName, 20, "0") +
                                    rightPad(a.assetType, 20, "0")
                            ).map((a, index) => {
                                return (
                                    <div
                                        className="webshop-asset-edit-page__asset-list-asset mt-1"
                                        key={index}
                                        onClick={() => {
                                            setAssetName(a.assetName)
                                            setAssetType(a.assetType)
                                            setCountryId(a.country)
                                            setPluginName(a.plugin)
                                        }}
                                    >
                                        <div>{a.country}</div>
                                        <div>{a.assetName}</div>
                                        <div>{a.assetType}</div>
                                        <div>{a.plugin}</div>
                                        <div>{a.version}</div>
                                    </div>
                                )
                            })}
                        </div>
                    </PageBoxSection>
                )}
            </PageContainer>
        </PageWrapper>
    )
}

export default WebshopAssetEditPage
